import React from "react";
import { v4 as uuidv4 } from "uuid";
import Slider from "react-slick";
import Lottie from "lottie-react";
import rocket from "../../img/rocket-white.json";
import error404 from "../../img/error-glitch.json";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import BookingDesktop from "./BookingDesktop";
import BookingMobile from "./BookingMobile";
import BookingShort from "./BookingShort";
import { useCourses } from "../../context/CourseContext";
import { useLocation } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";

const useStyles = (theme, isMobile) => ({
  titleContainer: {
    marginTop: isMobile ? "110.4px" : "262.3px",
    display: { md: "flex" },
    justifyContent: { md: "center" },
    alignItems: { md: "center" },
  },
  line: {
    width: "25px",
    border: `1px solid ${theme.palette.red.main}`,
    marginRight: "8px",
    backgroundColor: theme.palette.red.main,
  },
  inputs: {
    width: "100%",
    height: "fit-content",
    padding: "0",
    margin: "26px 0 0",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    select: {
      minWidth: "276px",
      height: "47px",
      borderRadius: "56px",
      boxShadow: "0 1px 12px 0 rgba(0, 0, 0, 0.1)",
      color: theme.palette.blue.main,
    },
  },
  sliderContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "visible",
    marginTop: "30px",
  },
  slider: {
    marginTop: isMobile ? "0" : "110px",
    width: "98%",
    height: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  },
  typography: {
    section: {
      fontSize: isMobile ? "14px" : "16px",
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
      marginTop: isMobile ? "59px" : "0",
    },
    sectionDescription: {
      fontSize: isMobile ? "32px" : { xl: "65px", md: "55px", xs: "50px" },
      fontFamily: theme.typography.montserrat.regular,
      color: theme.palette.blue.main,
      marginTop: isMobile ? "24px" : "10px",
      width: isMobile ? "170px" : "fit-content",
      subtitle: {
        fontSize: isMobile ? "25px" : { xl: "55px", md: "45px", xs: "40px" },
        color: theme.palette.blue.main,
        fontFamily: theme.typography.montserrat.regular,
      },
    },
  },
});

const lottieStyle = {
  height: 300,
};

const Booking = () => {
  const { introCourses, loading, error } = useCourses();
  const isMobile = useMediaQuery("(max-width:899px)");
  const theme = useTheme();
  const classes = useStyles(theme, isMobile);
  const { pathname } = useLocation();
  const { load, err, normal } = useTranslation(pathname, "Booking");

  if (loading) {
    return isMobile ? (
      <>
        <Typography sx={classes.typography.section}>{load.title}</Typography>
        <Typography sx={classes.typography.sectionDescription}>
          {load.subtitle[0]}
          <b>{load.subtitle[1]}</b>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Lottie animationData={rocket} loop={true} style={lottieStyle} />
          </Box>
        </Box>
      </>
    ) : (
      <>
        <Box sx={classes.titleContainer}>
          <div style={classes.line}></div>
          <Typography sx={classes.typography.section}>{load.title}</Typography>
        </Box>
        <Typography sx={classes.typography.sectionDescription}>
          {load.subtitle[0]}
          <b>{load.subtitle[1]}</b>
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Lottie animationData={rocket} loop={true} style={lottieStyle} />
          </Box>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          alignItems: "center",
          flexDirection: isMobile ? "column" : null,
          margin: "1rem",
        }}
      >
        <Box>
          <Lottie animationData={error404} loop={true} style={lottieStyle} />
        </Box>
        <Box
          sx={{
            flexWrap: "wrap",
            maxWidth: "40%",
          }}
        >
          <Typography
            sx={{
              fontFamily: theme.typography.anaHeim.regular,
              fontSize: "clamp(1rem, 2vw, 1.5rem)",
              color: theme.palette.red.main,
            }}
          >
            {err.title}
          </Typography>
        </Box>
      </Box>
    );
  }

  return isMobile ? (
    <>
      <Typography sx={classes.typography.section}>{normal.title}</Typography>
      <Typography sx={classes.typography.sectionDescription}>
        <b>{normal.subtitle}</b>
      </Typography>
      <Typography sx={classes.typography.sectionDescription.subtitle}>
        {normal.nextDates}
      </Typography>

      {introCourses &&
        introCourses.length &&
        introCourses
          .sort((prev) => (prev.status === "disponible" ? -1 : 1))
          .map((course) =>
            course.status === "disponible" ? (
              <Box style={classes.sliderContainer} key={uuidv4()}>
                <Slider
                  slidesToShow={1}
                  style={classes.slider}
                  autoplay={true}
                  autoplaySpeed={4000}
                  dots={true}
                >
                  <BookingMobile
                    info={course}
                    startDate={course.startDate}
                    endDate={course.endDate}
                    startHour={course.startHour}
                    endHour={course.endHour}
                    shift={course.shift}
                    weekDays={course.weekDays}
                    month={course.month}
                    year={course.year}
                    avialability={course.availability}
                    bootcampPreparation={course.bootcampPreparation}
                    name={course.type.name}
                    tag={course.type.tag}
                  />
                </Slider>
              </Box>
            ) : (
              <Box key={uuidv4()}>
                <BookingShort
                  info={course}
                  startDate={course.startDate}
                  endDate={course.endDate}
                  startHour={course.startHour}
                  endHour={course.endHour}
                  shift={course.shift}
                  weekDays={course.weekDays}
                  month={course.month}
                  year={course.year}
                  avialability={course.availability}
                  bootcampPreparation={course.bootcampPreparation}
                  name={course.type.name}
                  tag={course.type.tag}
                />
              </Box>
            )
          )}
    </>
  ) : (
    <>
      <Box sx={classes.titleContainer}>
        <div style={classes.line}></div>
        <Typography sx={classes.typography.section}>{normal.title}</Typography>
      </Box>
      <Typography sx={classes.typography.sectionDescription}>
        <b>{normal.subtitle}</b>
      </Typography>
      <Typography sx={classes.typography.sectionDescription.subtitle}>
        {normal.nextDates}
      </Typography>

      {introCourses
        .sort((prev) => (prev.status === "disponible" ? -1 : 1))
        .map((course) =>
          course.status === "disponible" ? (
            <BookingDesktop
              key={uuidv4()}
              info={course}
              startDate={course.startDate}
              endDate={course.endDate}
              startHour={course.startHour}
              endHour={course.endHour}
              shift={course.shift}
              weekDays={course.weekDays}
              month={course.month}
              year={course.year}
              avialability={course.availability}
              bootcampPreparation={course.bootcampPreparation}
              name={course.type.name}
              tag={course.type.tag}
              nextCourse={true}
            />
          ) : (
            <Box key={uuidv4()}>
              <BookingShort
                info={course}
                startDate={course.startDate}
                endDate={course.endDate}
                startHour={course.startHour}
                endHour={course.endHour}
                shift={course.shift}
                weekDays={course.weekDays}
                month={course.month}
                year={course.year}
                avialability={course.availability}
                bootcampPreparation={course.bootcampPreparation}
                name={course.type.name}
                tag={course.type.tag}
              />
            </Box>
          )
        )}
    </>
  );
};

export default Booking;
